import { Chat as ChatIcon } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import DiffViewer, { type DiffViewerHandle } from "~/components/aiChat/diff";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import documentStyles from "~/components/Document/documentStyles.module.scss";
import ExportModal, { type DocumentExportData } from "~/components/Document/exportModal";
import { MarkdownContent } from "~/components/Document/MarkdownContent";
import SystemProposalDisplay from "~/components/Document/SystemProposalDisplay";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import Modal from "~/components/ui/modal";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import { initializeSessions } from "~/slices/projectSlice";
import {
  acquireLock,
  addMainFunctionRow,
  addUsecaseTableRow,
  clearError,
  deleteMainFunctionRow,
  deleteUsecaseTableRow,
  fetchSystemProposal,
  releaseLock,
  resetData,
  updateArrayField,
  updateMarkdownContent,
  updateSystemProposal,
  updateSystemProposalContent,
  updateTable,
} from "~/slices/systemProposalSlice";
import type { AppDispatch, RootState } from "~/store";
import AIChat, { type AIChatHandle } from "../../components/aiChat";
import editStyles from "./index.module.scss";
import chatStyles from "./styles.module.scss";

const SystemProposal = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { selectedId } = useParams<{ selectedId: string }>();
  const { canAccessChat } = useProjectPermission();
  const { renderMarkdown } = useMarkdownRenderer();

  const { error: projectError } = useProjectMembers();
  const { data, isEditable, isLoading, error, documentInfo, markdownContent } = useSelector(
    (state: RootState) => state.systemProposalReducer,
  );

  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } =
    useKnowledgeCount("system-proposal");

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();

  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = useState(false);

  const { currentSession } = useSelector((state: RootState) => state.project);

  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAIChat, setShowAIChat] = useState(true);
  const [isDiffModalOpen, setDiffModalOpen] = useState(false);
  // 履歴サイドバー（チャット外・左隣）
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [threads, setThreads] = useState<any[]>([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const diffViewerRef = React.useRef<DiffViewerHandle>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string>("");
  const [documentBefore, setDocumentBefore] = useState<any>(null);
  const [documentAfter, setDocumentAfter] = useState<any>(null);
  const [isDiffSaving, setIsDiffSaving] = useState(false);
  const [diffViewerHeader, setDiffViewerHeader] = useState<React.ReactNode>(null);

  // マークダウンモードの状態
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);

  // プレビューエリアの幅管理
  const [previewWidth, setPreviewWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);

  // スクロールイベントの管理
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  const shouldUseMarkdownMode =
    isMarkdownMode || (markdownContent && markdownContent.trim() !== "");

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  const toggleEditMode = () => {
    if (isEditable) {
      handleUpdate();
    } else {
      dispatch(acquireLock());
    }
  };

  const handleCancel = async () => {
    await dispatch(releaseLock());
    await dispatch(resetData());
  };

  const handleUpdate = async () => {
    await dispatch(updateSystemProposal());
  };

  const handleExportClick = () => {
    if (markdownContent && markdownContent.trim() !== "") {
      setShowExportModal(true);
    } else if (data) {
      setShowExportModal(true);
    } else {
      console.error("data is empty");
    }
  };

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    handleUpdate,
    handleCancel,
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const systemProposalDocId = currentProject?.outputs?.["system-proposal"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(systemProposalDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, systemProposalDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[SystemProposal] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch system proposal data
  useEffect(() => {
    const documentId = selectedId || currentProject?.outputs?.["system-proposal"]?.id;
    if (documentId) {
      dispatch(fetchSystemProposal(documentId));
    }
  }, [selectedId, currentProject?.outputs?.["system-proposal"]?.id, dispatch]);

  // マークダウンコンテンツが存在する場合はマークダウンモードにする
  useEffect(() => {
    if (markdownContent && markdownContent.trim() !== "") {
      setIsMarkdownMode(true);
    }
  }, [markdownContent]);

  // リサイズハンドラー
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    const container = document.querySelector(
      `.${editStyles["edit-mode-container"]}`,
    ) as HTMLElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newPreviewWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;

    if (newPreviewWidth >= 20 && newPreviewWidth <= 80) {
      setPreviewWidth(newPreviewWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  const handleApprovedDocumentChange = async (approvedDocument: string) => {
    setIsDiffSaving(true);
    try {
      await dispatch(acquireLock());
      dispatch(updateMarkdownContent(approvedDocument));
      await dispatch(updateSystemProposalContent());
      await dispatch(fetchSystemProposal());
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsDiffSaving(false);
    }
  };

  const handleDiffClose = () => {
    setIsDiffSaving(false);
    setDiffModalOpen(false);
    setShowAIChat(false);
    setDocumentBefore(null);
    setDocumentAfter(null);
    setDiffViewerHeader(null);
  };

  const handleDiffApply = () => {
    if (isDiffSaving) return;
    void diffViewerRef.current?.applyChanges();
  };

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleMouseMove/handleMouseUp are stable inline functions that only depend on isResizing
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isSyncing.current) return;
    isSyncing.current = true;

    const scrolledElement = event.currentTarget;
    const targetElement = scrolledElement === leftRef.current ? rightRef.current : leftRef.current;

    if (targetElement) {
      const scrollableHeight = scrolledElement.scrollHeight - scrolledElement.clientHeight;
      const targetScrollableHeight = targetElement.scrollHeight - targetElement.clientHeight;

      if (scrollableHeight > 0) {
        const scrollPercentage = scrolledElement.scrollTop / scrollableHeight;
        targetElement.scrollTop = targetScrollableHeight * scrollPercentage;
      }

      const scrollableWidth = scrolledElement.scrollWidth - scrolledElement.clientWidth;
      const targetScrollableWidth = targetElement.scrollWidth - targetElement.clientWidth;

      if (scrollableWidth > 0) {
        const scrollPercentage = scrolledElement.scrollLeft / scrollableWidth;
        targetElement.scrollLeft = targetScrollableWidth * scrollPercentage;
      }
    }

    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  };

  const renderDocumentContent = (mode: "edit" | "preview" | "view") => {
    const effectiveIsEditable = mode === "edit";

    if (shouldUseMarkdownMode) {
      const markdownNode = (
        <div className={documentStyles["document-content"]}>
          <div className={documentStyles["markdown-mode-content"]}>
            {mode === "edit" ? (
              <textarea
                className={documentStyles["markdown-editor"]}
                value={markdownContent}
                onChange={(e) => dispatch(updateMarkdownContent(e.target.value))}
              />
            ) : (
              <MarkdownContent>{renderMarkdown(markdownContent || "")}</MarkdownContent>
            )}
          </div>
        </div>
      );

      if (mode === "view") {
        return (
          <div className={documentStyles["wrapper"]}>
            <div className={documentStyles["innerDiv"]}>{markdownNode}</div>
          </div>
        );
      }
      return markdownNode;
    }

    return (
      <SystemProposalDisplay
        data={data}
        isEditable={effectiveIsEditable}
        noWrapper={mode === "edit" || mode === "preview"}
        onArrayFieldChange={(field, value) => dispatch(updateArrayField({ field, value }))}
        onTableChange={(field, value) => dispatch(updateTable({ field, value }))}
        onAddUsecaseRow={() => dispatch(addUsecaseTableRow())}
        onDeleteUsecaseRow={(index) => dispatch(deleteUsecaseTableRow(index))}
        onAddMainFunctionRow={() => dispatch(addMainFunctionRow())}
        onDeleteMainFunctionRow={(index) => dispatch(deleteMainFunctionRow(index))}
      />
    );
  };

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  return (
    <>
      {showExportModal && (
        <ExportModal
          documentType="system-proposal"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "document",
            exportData: {
              content:
                markdownContent && markdownContent.trim() !== ""
                  ? markdownContent
                  : JSON.stringify(data, null, 2),
            } as DocumentExportData,
            name: "system-proposal",
          }}
        />
      )}

      <div>
        {documentInfo && (
          <>
            <CommonDocumentHeader
              title="システム概要書"
              documentType="system-proposal"
              createdAt={documentInfo.createdAt}
              modifiedAt={documentInfo.modifiedAt}
              {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
              isEditable={isEditable}
              isLoading={isLoading}
              handleCancel={handleCancel}
              toggleEditMode={toggleEditMode}
              handleExportClick={handleExportClick}
              handleHistoryClick={handleHistoryClick}
              showEditButton={true}
              showExportButton={true}
            />
            {false && (
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
                <KnowledgeIndicator
                  documentType="system-proposal"
                  activeCount={knowledgeCount}
                  totalCount={knowledgeTotalCount}
                />
              </div>
            )}
          </>
        )}

        {isEditable && !isDiffSaving ? (
          <div className={editStyles["edit-mode-container"]}>
            <div
              className={editStyles["edit-area"]}
              style={{ width: `${100 - previewWidth}%` }}
              ref={leftRef}
              onScroll={handleScroll}
            >
              {renderDocumentContent("edit")}
            </div>
            <div className={editStyles["resize-handle"]} onMouseDown={handleMouseDown} />
            <div
              className={editStyles["preview-area"]}
              style={{ width: `${previewWidth}%` }}
              ref={rightRef}
              onScroll={handleScroll}
            >
              {renderDocumentContent("preview")}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: "24px",
              alignItems: "stretch",
              position: "relative",
              minHeight: "calc(100vh - 196px)",
            }}
          >
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>{renderDocumentContent("view")}</div>
            {!showAIChat && (
              <KnowledgeSidebar
                isOpen={isKnowledgeSidebarOpen}
                onClose={() => setIsKnowledgeSidebarOpen(false)}
                documentType="system-proposal"
              />
            )}
            {showAIChat && (
              <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
                <ChatHistoryPanel
                  open={showChatHistory}
                  placement="sidebar"
                  title="履歴（最新10件）"
                  loading={historyLoading}
                  threads={threads}
                  activeThreadId={currentThreadId}
                  onClose={() => setShowChatHistory(false)}
                  onRefresh={refreshThreadHistory}
                  onLoadThread={async (t) => {
                    const tid = t.id || t.threadId;
                    if (tid) {
                      setCurrentThreadId(tid);
                    }
                    await aiChatRef.current?.loadThread(t, "agent");
                  }}
                />
                <AIChat
                  ref={aiChatRef}
                  isOpen={showAIChat}
                  onClose={() => setShowAIChat(false)}
                  onPreviewDiff={(before, after) => {
                    setDocumentBefore(before);
                    setDocumentAfter(after);
                    setDiffModalOpen(before != null && after != null);
                  }}
                  height="calc(100vh - 196px)"
                  maxHeight="none"
                  currentDocumentId={currentProject?.outputs?.["system-proposal"]?.id || ""}
                  currentProjectId={currentProject?.id || ""}
                  currentDocumentType="system-proposal"
                  originalDocument={markdownContent}
                  threadId={currentSession?.threadId}
                  resourceId={currentSession?.resourceId}
                  onOpenHistory={async () => {
                    setShowChatHistory(true);
                    await refreshThreadHistory();
                  }}
                  onThreadUpdate={refreshThreadHistory}
                  onThreadChange={(tid) => setCurrentThreadId(tid)}
                />
              </div>
            )}
          </div>
        )}

        {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
          <div className={chatStyles["floatingButtons"]}>
            <KnowledgeIndicator
              documentType="system-proposal"
              activeCount={knowledgeCount}
              totalCount={knowledgeTotalCount}
              compact
              onOpen={() => setIsKnowledgeSidebarOpen(true)}
            />
            <button
              className={chatStyles["chatButton"]}
              onClick={() => setShowAIChat(true)}
              aria-label="AIチャットを開く"
            >
              <ChatIcon />
            </button>
          </div>
        )}

        <Modal
          label="差分プレビュー"
          isOpen={isDiffModalOpen && !!documentBefore && !!documentAfter}
          onClose={handleDiffClose}
          className="diffPreviewModal"
          usePortal
          subHeader={diffViewerHeader}
          content={
            !!documentBefore && !!documentAfter ? (
              <div className={chatStyles["diffModalContent"]}>
                <div className={chatStyles["diffModalScroll"]}>
                  <DiffViewer
                    ref={diffViewerRef}
                    originalContent={
                      typeof documentBefore === "string"
                        ? documentBefore
                        : JSON.stringify(documentBefore, null, 2)
                    }
                    updatedContent={
                      typeof documentAfter === "string"
                        ? documentAfter
                        : JSON.stringify(documentAfter, null, 2)
                    }
                    onApprovedDocumentChange={handleApprovedDocumentChange}
                    onClose={handleDiffClose}
                    renderHeaderExternally
                    onHeaderChange={setDiffViewerHeader}
                  />
                </div>
              </div>
            ) : null
          }
          footer={
            <div className={chatStyles["diffModalFooter"]}>
              <button
                type="button"
                className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonSecondary"]}`}
                onClick={handleDiffClose}
                disabled={isDiffSaving}
              >
                キャンセル
              </button>
              <button
                type="button"
                className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonPrimary"]}`}
                onClick={handleDiffApply}
                disabled={isDiffSaving || !documentBefore || !documentAfter}
              >
                反映する
              </button>
            </div>
          }
        />
      </div>
    </>
  );
};

export default SystemProposal;
